// ─── NuLife PA — the owner's app ──────────────────────────────────────────────
//
// Built 2026-10-04 night, reusing the exact proven shell shape from Crafting
// Comb's own App.tsx (same `Shell`/`data-nl-theme` gotcha, same path-based
// routing, same lazy-loading discipline) — not reinvented, because this app's
// screens (Home, Diary, Clients, Setup) are built from the exact same
// `@nulife/home-ui`/`@nulife/booking-ui` components Crafting Comb's own
// screens already use.
//
// Added 2026-10-05 (night): `/book/:slug` is a genuine second surface,
// reached by a client with no session at all — the same shape Crafting
// Comb's own App.tsx already has (`Customer` renders regardless of sign-in
// state; only the owner screens gate on it). This is DIFFERENT from the
// voice-only public demo page at `/demo/:slug` (a separate static page, see
// `receptionist.ts`/`elevenlabs.ts`) — that one talks to the AI receptionist;
// this one is `@nulife/booking-ui`'s own `BookingJourney`, a real self-
// service booking flow, no voice involved.
//
// `useAsync(... )` for `me` is still called unconditionally even on
// `/book/:slug` — React's own rule that hooks run in the same order every
// render means an early return before the HOOK CALL would be a real bug the
// moment somebody navigates from `/book/:slug` to `/home` without a full
// page reload. What the hook is given to run IS conditional, though, and has
// to be: a real bug caught live before shipping, 2026-10-05 night — calling
// `api.getMe()` unconditionally meant it always 401s on the public page
// (correctly: nobody is signed in there), which fires `whenSignedOut`,
// which calls `me.reload()`, which calls `getMe()` again, which 401s again
// — hundreds of requests a second, confirmed via a real Playwright console
// check, not assumed safe from the UI looking fine. Skipping the real
// network call (not just skipping the branch that reads its result) when on
// the public page is what actually stops the loop.

import { Suspense, lazy, useEffect, useRef, useState } from "react";
import "@nulife/booking-ui/theme.css";
import { PA_THEME } from "./paTheme.js";
import "@nulife/home-ui/theme.css";
import { SignInForm, useAsync, Loading, Message } from "@nulife/app-ui";
import * as api from "./api.js";

const Home = lazy(() => import("./Home.js").then(m => ({ default: m.Home })));
const Diary = lazy(() => import("./Diary.js").then(m => ({ default: m.Diary })));
const Clients = lazy(() => import("./Clients.js").then(m => ({ default: m.Clients })));
const Setup = lazy(() => import("./Setup.js").then(m => ({ default: m.Setup })));
const PaRouter = lazy(() => import("./PaRouter.js").then(m => ({ default: m.PaRouter })));
const PA_PATHS = ["/messages", "/tasks", "/alerts", "/calendar", "/contacts", "/capture", "/media", "/documents", "/social", "/reminders", "/reminders-demo"];
const isPaPath = (p: string) => PA_PATHS.some(x => p === x || p.startsWith(`${x}/`));
const AcceptInvite = lazy(() => import("./AcceptInvite.js").then(m => ({ default: m.AcceptInvite })));
const PublicBooking = lazy(() => import("./PublicBooking.js").then(m => ({ default: m.PublicBooking })));

function usePath(): string {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return path;
}

export function App() {
  const path = usePath();
  const home = path === "/" || path === "/home" || path === "/home/";
  const diary = path.startsWith("/diary");
  const clients = path.startsWith("/clients");
  const setup = path.startsWith("/setup");
  const bookSlug = path.startsWith("/book/") ? path.slice("/book/".length).replace(/\/$/, "") : null;
  /** `/welcome/<token>` — an access link. Reached by somebody with no session
   *  (or the wrong one), so like `/book/:slug` it must not ask who is signed in. */
  const welcomeToken = path.startsWith("/welcome/")
    ? decodeURIComponent(path.slice("/welcome/".length).replace(/\/$/, "")) || ""
    : null;
  const skipMe = bookSlug !== null || welcomeToken !== null;

  const go = (to: string) => {
    window.history.pushState({}, "", to);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  // Called unconditionally, every render, same as `usePath` above — see this
  // file's own header on why an early return before this would be a bug,
  // and why the real fix for the public page is in what's passed to run,
  // not in skipping the hook call itself.
  const me = useAsync(
    () => (skipMe ? Promise.resolve({ signedIn: false } as api.Me) : api.getMe()),
    [skipMe],
  );
  // A 401 means "your session ended" ONLY if there was a session. The sign-in
  // page's own `/me` 401 is the normal answer to "who am I?" for somebody not
  // signed in; reloading on it asked again, got the same 401, and looped at
  // ~125 requests a second for as long as the page was open (found 2026-10-25).
  // So the announcement is acted on only after a signed-in answer.
  const wasSignedIn = useRef(false);
  wasSignedIn.current = me.data?.signedIn === true;
  useEffect(() => {
    if (skipMe) return;
    api.whenSignedOut(() => { if (wasSignedIn.current) me.reload(); });
  }, [me.reload, skipMe]);

  // An access link takes precedence over any existing session: somebody already
  // signed in as themselves who opens a link meant for somebody else must see
  // whose it is and decide, not have it swallowed by the app rendering Home.
  if (welcomeToken !== null) {
    return (
      <Suspense fallback={<Shell><Loading /></Shell>}>
        <AcceptInvite token={welcomeToken} onIn={() => go("/messages")} />
      </Suspense>
    );
  }

  if (bookSlug) {
    return (
      <Shell>
        <Suspense fallback={<Loading />}>
          <PublicBooking slug={bookSlug} />
        </Suspense>
      </Shell>
    );
  }

  if (me.loading && !me.data) {
    return <Shell><Loading /></Shell>;
  }
  if (me.error) {
    return <Shell><Message tone="bad">Could not reach the server. Please refresh.</Message></Shell>;
  }

  if (!me.data?.signedIn) {
    return (
      <Shell>
        <SignInForm
          organisationName={me.data?.businessName ?? "NuLife PA"}
          heading="Owner sign in"
          submit={api.signIn}
          onSignedIn={() => me.reload()}
          resetHint={<>Forgotten it? Ask whoever set this account up to reset it.</>}
        />
      </Shell>
    );
  }

  return (
    // No per-org theme here — the owner dashboard matches Crafting Comb's
    // real app exactly, including its real (unthemed, dark) base palette.
    // The `bookingUiTheme` field this used to read has been removed
    // entirely (api.ts), not just unused here — it had no other consumer.
    // /demo/:slug is a separate static surface (receptionist.ts/
    // elevenlabs.ts), not part of this React app at all, and /book/:slug
    // (PublicBooking, below) already rendered through an untheme-d Shell
    // before this change — neither public surface is affected.
    // An organisation switched on for the personal-assistant modules gets those
    // screens, rendered full-width because the shell they are built on brings
    // its own frame. Everybody else is unchanged: the code below is exactly
    // what it was.
    me.data.isGuest || ((me.data.modules ?? []).length > 0 && (home || isPaPath(path))) ? (
      <Shell bleed theme={PA_THEME}>
        <Suspense fallback={<Loading />}>
          <PaRouter me={me.data} path={home ? "/home" : path} onGo={go} onSignedOut={() => me.reload()} />
        </Suspense>
      </Shell>
    ) : (
    <Shell>
      <Suspense fallback={<Loading />}>
        {home
          ? <Home me={me.data} onGo={go} onSignedOut={() => me.reload()} />
          : diary
            ? <Diary me={me.data} onGo={go} />
            : clients
              ? <Clients me={me.data} onGo={go} />
              : setup
                ? <Setup me={me.data} onGo={go} />
                : <Home me={me.data} onGo={go} onSignedOut={() => me.reload()} />}
      </Suspense>
      {!home && (
        <footer style={{ marginTop: 40, color: "var(--nl-ink-dim)", fontSize: 13 }}>
          <a
            href="/home" style={{ color: "inherit" }}
            onClick={e => { e.preventDefault(); go("/home"); }}
          >Home</a>
        </footer>
      )}
    </Shell>
    )
  );
}

/**
 * The app's visual root. Same real gotcha as Crafting Comb's own `Shell`:
 * `@nulife/booking-ui`'s theme.css only declares its tokens under
 * `[data-nl-theme]` — the attribute has to be PRESENT even with nothing to
 * override, or every `var(--nl-*)` resolves to nothing and the whole thing
 * renders unstyled.
 */
function Shell({ theme, bleed, children }: { theme?: string | null; bleed?: boolean; children: React.ReactNode }) {
  return (
    <div
      data-nl-theme={theme || "nulife"}
      style={{
        minHeight: "100vh", background: "var(--nl-bg)", color: "var(--nl-ink)",
        fontFamily: "var(--nl-font)", fontSize: "1rem", lineHeight: 1.45,
      }}
    >
      {bleed ? children : (
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "20px 16px 64px" }}>
          {children}
        </div>
      )}
    </div>
  );
}
